"use client";

import {
	alreadyTrackedRecently,
	getOrCreateVisitorId,
} from "@klarify/ui/components/clinic-stats/visitor-id";
import { usePathname } from "@klarify/ui/i18n/navigation";
import { api } from "@klarify/ui/trpc/client";
import { classifyTrafficSource } from "@klarify/utilities";
import { useEffect } from "react";

/**
 * Generic page-land tracker for the public marketing surfaces
 * (/therapists, /clinics, /blogs). Mount once per public layout; it fires a
 * server-side `public_page_viewed` event on every pathname change, so
 * client-side navigations between directory pages count as distinct views.
 *
 * Goes through the `trackPublicPageview` server proxy because the browser
 * Mixpanel/PostHog SDKs are opted out until cookie consent — anonymous SEO
 * traffic would otherwise be invisible. No cookies are set; the visitor id
 * is the same localStorage UUID the therapist Stats trackers use.
 */
export function TrackPublicPageView() {
	const pathname = usePathname();
	const track = api.trackingRouter.trackPublicPageview.useMutation();

	useEffect(() => {
		if (!pathname) {
			return;
		}
		// 30-min per-path dedup so back-nav and remounts don't inflate counts.
		if (alreadyTrackedRecently(`public-page:${pathname}`)) {
			return;
		}
		const source = classifyTrafficSource(
			document.referrer || undefined,
			window.location.hostname,
		);
		track.mutate({
			distinctId: getOrCreateVisitorId(),
			event: "public_page_viewed",
			properties: {
				path: pathname,
				source,
				locale: document.documentElement.lang || undefined,
			},
		});
	}, [pathname, track]);

	return null;
}
